import { NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { GraduationCap, LayoutDashboard, BookOpen, FilePlus2, LogOut, Users, Shield, UserCog, Settings, Crown } from "lucide-react";

const baseNav = [
  { to: "/dashboard", label: "Tableau de bord", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/creer", label: "Créer un rattrapage", icon: FilePlus2, testId: "nav-create" },
  { to: "/bibliotheque", label: "Bibliothèque d'exemples", icon: BookOpen, testId: "nav-library" },
];

const adminNav = [
  { to: "/admin/utilisateurs", label: "Utilisateurs", icon: Users, testId: "nav-admin-users" },
];

const superNav = [
  { to: "/admin/configuration", label: "Configuration système", icon: Settings, testId: "nav-system-settings" },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin" || user?.role === "superadmin";
  const isSuper = user?.role === "superadmin";

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-[#f8f9fa]">
      <aside className="w-72 shrink-0 border-r border-slate-200 bg-white flex flex-col" data-testid="app-sidebar">
        <div className="px-6 pt-7 pb-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-sm bg-[#0f172a] flex items-center justify-center">
              <GraduationCap size={18} className="text-white" />
            </div>
            <div>
              <div className="font-display font-bold text-[15px] leading-tight text-slate-900">
                Rattrapage Builder
              </div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold mt-0.5">
                École d'Ingénieurs
              </div>
            </div>
          </div>
        </div>

        <nav className="px-3 py-4 flex-1 space-y-1">
          {baseNav.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testId}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
                    isActive
                      ? "bg-slate-900 text-white font-semibold"
                      : "text-slate-700 hover:bg-slate-100"
                  }`
                }
              >
                <Icon size={16} strokeWidth={1.8} />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
          {isAdmin && (
            <div className="pt-4 mt-4 border-t border-slate-100">
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400 flex items-center gap-1.5">
                <Shield size={10} /> Administration
              </div>
              {adminNav.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    data-testid={item.testId}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
                        isActive
                          ? "bg-slate-900 text-white font-semibold"
                          : "text-slate-700 hover:bg-slate-100"
                      }`
                    }
                  >
                    <Icon size={16} strokeWidth={1.8} />
                    <span>{item.label}</span>
                  </NavLink>
                );
              })}
              {isSuper && superNav.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    data-testid={item.testId}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
                        isActive
                          ? "bg-slate-900 text-white font-semibold"
                          : "text-slate-700 hover:bg-slate-100"
                      }`
                    }
                  >
                    <Icon size={16} strokeWidth={1.8} />
                    <span>{item.label}</span>
                    <Crown size={11} className="ml-auto text-amber-500" />
                  </NavLink>
                );
              })}
            </div>
          )}
        </nav>

        <div className="border-t border-slate-200 p-4 space-y-3">
          <Link
            to="/mon-compte"
            data-testid="nav-account"
            className="block px-1 hover:bg-slate-50 rounded-sm -mx-1 px-3 py-2 transition-colors"
          >
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 flex items-center justify-between">
              <span>Mon compte</span>
              <UserCog size={11} />
            </div>
            <div className="text-sm font-semibold text-slate-900 mt-1 truncate flex items-center gap-1.5" data-testid="user-name">
              {user?.name}
              {isSuper ? <Crown size={11} className="text-amber-500" /> : isAdmin && <Shield size={11} className="text-[#2563eb]" />}
            </div>
            <div className="text-xs text-slate-500 truncate" data-testid="user-email">{user?.email}</div>
          </Link>
          <button
            onClick={onLogout}
            data-testid="logout-btn"
            className="w-full flex items-center gap-2 justify-center px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 border border-slate-200 rounded-sm transition-colors"
          >
            <LogOut size={14} />
            Se déconnecter
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 overflow-y-auto">{children}</main>
    </div>
  );
}
